import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, type CSSProperties, type ReactNode } from 'react'
import { ApiError, type PostValue } from '../../api/client'
import { autonomyPost, autonomyQuery, type Answer, type AutonomyPage as Page, type Row } from '../../api/settingsAutonomy'
import { Spinner } from '../../ui/Spinner'
import { AutonomyResponsePanel } from './AutonomyResponsePanel'
import { AskChannelPanel } from './AskChannelPanel'
import { AutonomyDeskPanel } from './AutonomyDeskPanel'
import { AuthorityMatrixPanel } from './AuthorityMatrixPanel'
import { DeskMetricsPanel } from './DeskMetricsPanel'
import { DeskTextsPanel } from './DeskTextsPanel'
import { MailIntakePanel } from './MailIntakePanel'
import { AutonomyOverview } from './AutonomyOverview'
import '../../desk/deskScreens.css'
import './autonomyTables.css'
import './autonomyLayout.css'

/**
 * Settings -> Policies -> Agent Autonomy.
 *
 * Doua niveluri, alese o data, sus: firma din bara de sus (`scope`/`dscope`
 * goale) sau "All companies" (`scope=sp&dscope=sp`, numai Global Admin). Tot ce
 * e sub alegere scrie pe nivelul ales; ce tine de toata platforma (comutatorul
 * general, pauza biroului, plasele, senzorii) sta in bara platformei sau numai
 * la "All companies". Tab-urile tin toate panourile montate (`hidden`), ca un
 * formular neterminat sa nu se piarda la schimbarea tab-ului.
 */
export const POSTURE: Record<string, [string, string, string, string]> = {
  shadow: ['Shadow', '#5b6672', '#eef0f2', 'only logs what it would have done'],
  propose: ['Propose', '#1f5f8b', '#e3f0fa', 'drafts a proposal; a person approves it'],
  remediate: ['Remediate', '#b42318', '#fdecea', 'runs the catalog action there and then'],
}
const SEV_COLOR: Record<string, string> = { critical: '#b42318', major: '#b54708', minor: '#6b7480', info: '#6b7480' }
const DAYS: [string, string][] = [['1', 'Mon'], ['2', 'Tue'], ['3', 'Wed'], ['4', 'Thu'], ['5', 'Fri'], ['6', 'Sat'], ['7', 'Sun']]

const pill = (bg: string, fg: string): CSSProperties => ({ background: bg, color: fg, padding: '2px 10px', borderRadius: 10, fontSize: 11, fontWeight: 600 })
const muted: CSSProperties = { fontSize: 12 }
export const s = (v: unknown): string => String(v ?? '')
export const n = (v: unknown): number => Number(v ?? 0)
/** Latimea sub care un tabel `.odau-table` se deruleaza in cutia lui (vezi `autonomyTables.css`). */
export const tableMin = (em: number) => ({ '--odau-min': `${em}em` }) as CSSProperties

/** `$m.text('...')`-ul de langa fiecare buton: gri cat se cere, verde/rosu dupa. */
export function useMsg(): [ReactNode, (text: string, tone: 'wait' | 'ok' | 'bad') => void] {
  const [state, set] = useState<{ text: string; tone: 'wait' | 'ok' | 'bad' } | null>(null)
  const node = state ? <span style={{ fontSize: 12, color: state.tone === 'ok' ? '#2e7d32' : state.tone === 'bad' ? '#b42318' : '#6b7480' }}>{state.text}</span> : null
  return [node, (text, tone) => set({ text, tone })]
}

export function post(path: string, fields: Record<string, PostValue>, say: (t: string, tone: 'wait' | 'ok' | 'bad') => void, done?: (r: Answer) => void, okText = 'Saved') {
  say('...', 'wait')
  void autonomyPost(path, fields)
    .then((r) => {
      if (r.ok) {
        say(okText, 'ok')
        done?.(r)
      } else say(r.error || 'Failed', 'bad')
    })
    .catch((e: unknown) => say(e instanceof ApiError && e.message ? e.message : 'Failed', 'bad'))
}

export type Level = 'company' | 'all'
export type Tab = 'overview' | 'desk' | 'channels' | 'alarms' | 'texts' | 'metrics'
const TABS: [Tab, string][] = [
  ['overview', 'Overview'],
  ['desk', 'Service desk'],
  ['channels', 'Channels'],
  ['alarms', 'Alarms & rules'],
  ['texts', 'Texts'],
  ['metrics', 'Metrics'],
]
/* Ancorele vechi (`#odmi` din emailul "mailbox failing", `#odsk` din legaturile biroului) duc la tab-ul lor. */
const HASH_TAB: [string, Tab][] = [['odmi', 'channels'], ['odask', 'channels'], ['odsk-metrics', 'metrics'], ['odsk', 'desk'], ['odar', 'alarms'], ['odau', 'alarms'], ['oddt', 'texts']]

interface Loc { level: Level; tab: Tab }

export function readLocation(): Loc {
  const params = new URLSearchParams(window.location.search)
  const level: Level = params.get('level') === 'all' || params.get('scope') === 'sp' || params.get('dscope') === 'sp' ? 'all' : 'company'
  const asked = params.get('tab')
  const hash = window.location.hash.replace(/^#/, '')
  const tab = TABS.some(([t]) => t === asked) ? (asked as Tab) : (HASH_TAB.find(([h]) => hash.startsWith(h))?.[1] ?? 'overview')
  return { level, tab }
}

function writeLocation(loc: Loc) {
  const p = new URLSearchParams()
  if (loc.level === 'all') p.set('level', 'all')
  if (loc.tab !== 'overview') p.set('tab', loc.tab)
  const q = p.toString()
  window.history.replaceState(window.history.state, '', `${window.location.pathname}${q ? `?${q}` : ''}`)
}

export function AutonomyPage() {
  const client = useQueryClient()
  const [loc, setLoc] = useState<Loc>(readLocation)
  const sp = loc.level === 'all' ? 'sp' : ''
  const page = useQuery(autonomyQuery(sp, sp))

  if (page.error instanceof ApiError && page.error.status === 403) {
    return (
      <div className="alert alert-danger">
        <i className="fas fa-exclamation-circle" aria-hidden="true"></i> Forbidden
      </div>
    )
  }
  if (!page.data) return <Spinner />
  const d = page.data
  const reload = () => client.invalidateQueries({ queryKey: ['settings', 'autonomy'] })
  const level: Level = d.isGlobal && loc.level === 'all' ? 'all' : 'company'
  const tab = loc.tab
  const go = (next: Partial<Loc>) => {
    const l = { ...loc, ...next }
    setLoc(l)
    writeLocation(l)
  }
  const deskPaused = d.deskPaused?.paused === true
  const companyLabel = d.companyName || `company #${d.companyId}`

  return (
    <div className="row od-desk">
      <div className="col-md-11 col-md-offset-1">
        <div className="odx" data-level={level}>
          <div className="odx-head">
            <h2>Agent Autonomy</h2>
            <p>What the AI does on its own: answer tickets, react to alarms, run rules.</p>
          </div>

          {deskPaused && (
            <div className="panel panel-default" id="odau-desk-paused" style={{ borderLeft: '4px solid #b42318' }}>
              <div className="panel-body" style={{ background: '#fdecea' }}>
                <strong style={{ color: '#b42318' }}>The AI desk is paused</strong>
                {d.deskPaused?.by ? <> by user #{d.deskPaused.by}</> : null}
                {d.deskPaused?.at ? <> since {d.deskPaused.at}</> : null}
                {d.deskPaused?.reason ? <>: {d.deskPaused.reason}</> : null}
                . No new run starts; machine tools refuse; desk replies stay queued. Mailboxes are still read.
              </div>
            </div>
          )}
          {!d.ready && (
            <div className="panel panel-default" style={{ borderLeft: '4px solid #b42318' }}>
              <div className="panel-body" style={{ background: '#fdecea' }}>
                <strong style={{ color: '#b42318' }}>The Autonomy schema has not been applied.</strong> Apply it in one click from{' '}
                <a href="/app/settings/change_log">Change Log &rarr; Database migrations</a>, then come back here.
              </div>
            </div>
          )}

          <PlatformBar d={d} onChange={reload} />

          {d.isGlobal ? (
            <div className="odx-level" role="radiogroup" aria-label="What you are editing">
              <button type="button" role="radio" aria-checked={level === 'company'} className="odx-level-btn" id="odx-level-company" onClick={() => go({ level: 'company' })}>
                <i className="fas fa-building" aria-hidden="true"></i>
                <div><strong>{companyLabel}</strong><span>this company only &middot; pick another one in the top bar</span></div>
              </button>
              <button type="button" role="radio" aria-checked={level === 'all'} className="odx-level-btn" id="odx-level-all" onClick={() => go({ level: 'all' })}>
                <i className="fas fa-globe" aria-hidden="true"></i>
                <div><strong>All companies</strong><span>the defaults every company starts from, and the platform settings</span></div>
              </button>
            </div>
          ) : null}
          <p className="odx-scope-note" id="odx-scope-note">
            {level === 'all' ? (
              <>You are editing the <strong>defaults for every company</strong>. A company that saves its own settings keeps them; the others follow what you set here.</>
            ) : (
              <>You are editing <strong>{companyLabel}</strong>. Whatever you do not change here comes from the defaults for all companies.</>
            )}
          </p>

          <div className="odx-tabs" role="tablist" aria-label="Agent Autonomy">
            {TABS.map(([t, label]) => (
              <button key={t} type="button" role="tab" id={`odx-tab-${t}`} aria-selected={tab === t} aria-controls={`odx-pane-${t}`} className="odx-tab" onClick={() => go({ tab: t })}>
                {label}
              </button>
            ))}
          </div>

          <div key={level}>
            <section role="tabpanel" id="odx-pane-overview" aria-labelledby="odx-tab-overview" hidden={tab !== 'overview'}>
              <AutonomyOverview d={d} level={level} onOpen={(t) => go({ tab: t })} />
            </section>

            <section role="tabpanel" id="odx-pane-desk" aria-labelledby="odx-tab-desk" hidden={tab !== 'desk'}>
              <p className="odx-pane-intro">
                A ticket written by a person goes to the AI agent: it reads their machine, does what it is allowed to, answers in their language and resolves the ticket. What it cannot or may not do goes to a colleague with the diagnosis written.
              </p>
              <AutonomyDeskPanel k={d.desk} isGlobal={d.isGlobal} companyId={d.companyId} companyName={d.companyName} onChange={reload} onOpenTab={(t) => go({ tab: t })} />
              <AuthorityMatrixPanel scope={d.desk.scope} isGlobal={d.isGlobal} companyName={d.companyName} onChange={reload} />
            </section>

            <section role="tabpanel" id="odx-pane-channels" aria-labelledby="odx-tab-channels" hidden={tab !== 'channels'}>
              <p className="odx-pane-intro">
                Where requests come from besides the agent window: {level === 'all' ? 'the provider mailbox, used for every company without one of its own,' : 'the company mailbox,'} the customer app and the station chat. What the desk may do with them is set in Service desk.
              </p>
              <MailIntakePanel scope={d.desk.scope} companyId={d.companyId} companyName={d.companyName} />
              <AskChannelPanel k={d.desk} companyId={d.companyId} companyName={d.companyName} onChange={reload} />
            </section>

            <section role="tabpanel" id="odx-pane-alarms" aria-labelledby="odx-tab-alarms" hidden={tab !== 'alarms'}>
              <AutonomyResponsePanel r={d.response} isGlobal={d.isGlobal} companyId={d.companyId} companyName={d.companyName} onChange={reload} />
              {level === 'all' ? (
                <>
                  <GuardsPanel d={d} onChange={reload} />
                  <SensorsPanel d={d} />
                </>
              ) : (
                <details className="odx-fold" id="odau-sensors-fold">
                  <summary>Sensors that raise alarms <span className="text-muted" style={{ fontWeight: 400 }}>&middot; the same for every company</span></summary>
                  <div className="odx-fold-body"><SensorsPanel d={d} /></div>
                </details>
              )}
              <RulesSection d={d} level={level} onChange={reload} />
            </section>

            <section role="tabpanel" id="odx-pane-texts" aria-labelledby="odx-tab-texts" hidden={tab !== 'texts'}>
              <DeskTextsPanel scope={d.desk.scope} companyName={d.companyName} />
            </section>

            <section role="tabpanel" id="odx-pane-metrics" aria-labelledby="odx-tab-metrics" hidden={tab !== 'metrics'}>
              <DeskMetricsPanel dscope={d.desk.scope} />
            </section>
          </div>
        </div>
      </div>
    </div>
  )
}

/** Ce e valabil pe toata platforma, oricare ar fi nivelul: comutatorul general, pauza biroului, bataia inimii. */
function PlatformBar({ d, onChange }: { d: Page; onChange(): void }) {
  const [switchMsg, saySwitch] = useMsg()
  const [pauseMsg, sayPause] = useMsg()
  const on = d.switchedOn
  const deskOnPause = d.deskPaused?.paused === true
  const beats = d.heartbeat ?? []
  return (
    <div className="odx-platform" id="odau-platform">
      <span className="odx-platform-tag">Platform</span>
      <span className="odx-platform-item">
        <strong>Autonomy master switch</strong>
        <span className={`odx-pill ${on ? 'is-good' : 'is-warn'}`}>{on ? 'ON' : 'OFF'}</span>
        {d.isGlobal && (
          <button type="button" className={`btn btn-xs ${on ? 'btn-default' : 'btn-success'}`} id="odau-switch" onClick={() => post('/switch', { on: on ? '0' : '1' }, saySwitch, () => onChange(), on ? 'OFF' : 'ON')}>
            {on ? 'Turn OFF' : 'Turn ON'}
          </button>
        )}
        {switchMsg}
      </span>
      <span className="odx-platform-item">
        <strong>AI desk</strong>
        <span className={`odx-pill ${deskOnPause ? 'is-bad' : 'is-good'}`}>{deskOnPause ? 'Paused' : 'Running'}</span>
        {d.isGlobal && (
          <button
            type="button"
            className={`btn btn-xs ${deskOnPause ? 'btn-success' : 'btn-danger'}`}
            id="odau-desk-pause"
            onClick={() => {
              if (deskOnPause) {
                post('/desk/pause', { on: '0', reason: '' }, sayPause, () => onChange(), 'Resumed')
                return
              }
              const reason = window.prompt('Why is the AI desk paused?', '') ?? ''
              post('/desk/pause', { on: '1', reason }, sayPause, () => onChange(), 'Paused')
            }}
          >
            {deskOnPause ? 'Resume the AI' : 'Pause the AI'}
          </button>
        )}
        {pauseMsg}
      </span>
      <span id="odau-heartbeat" className="odx-platform-item" style={{ color: beats.some((h) => h.stale) ? '#b42318' : '#6b7480' }}>
        Last tick:{' '}
        {beats.length === 0 ? (
          <span className="text-muted">not recorded yet</span>
        ) : (
          <span>
            {beats.map((h, i) => {
              const when = h.finished_at || h.started_at
              const time = when ? when.slice(11, 19) : '—'
              const ok = !h.last_error && !!h.last_ok_at && h.last_ok_at === h.finished_at
              const label = h.stale ? 'stale' : ok ? 'ok' : (h.last_error ? 'error' : '—')
              return (
                <span key={h.tick} style={{ color: h.stale ? '#b42318' : undefined }}>
                  {i > 0 ? ' · ' : ''}
                  {h.tick} {time} ({label})
                </span>
              )
            })}
          </span>
        )}
      </span>
      <span className="odx-platform-note">
        {d.isGlobal ? '' : 'Only a Global Admin can flip it. '}
        While the master switch is OFF no rule fires, however many there are. Pausing the AI stops the service desk for every company; mailboxes keep being read.
      </span>
    </div>
  )
}

/** Plasele de siguranta ale regulilor - numai la "All companies", numai Global Admin. */
function GuardsPanel({ d, onChange }: { d: Page; onChange(): void }) {
  const [notifyMsg, sayNotify] = useMsg()
  const [diagMsg, sayDiag] = useMsg()
  const [capMsg, sayCap] = useMsg()
  const [email, setEmail] = useState(d.notifyEmail)
  const [cap, setCap] = useState(String(d.dailyCap ?? 50))
  const row: CSSProperties = { display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginTop: 8 }
  return (
    <div className="panel panel-default" id="odau-guards">
      <div className="panel-body">
        <strong style={{ fontSize: 14 }}>Guards for every company</strong>
        <span className="text-muted" style={{ ...muted, marginLeft: 8 }}>who hears about it, what gets a diagnosis on its own, and how many actions a day are too many.</span>
        <div style={row}>
          <label htmlFor="odau-notify" style={{ margin: 0, fontSize: 13, minWidth: '9em' }}><strong>Notify email</strong></label>
          <input type="email" className="form-control" id="odau-notify" style={{ width: 280, maxWidth: '100%' }} value={email} onChange={(e) => setEmail(e.target.value)} placeholder="who hears when Autonomy proposes or remediates" />
          <button type="button" className="btn btn-default btn-sm" id="odau-notify-save" onClick={() => post('/notify', { email }, sayNotify)}>Save</button>
          {notifyMsg}
          <span className="text-muted" style={muted}>empty = no email. Shadow never notifies; the log has all of it anyway.</span>
        </div>
        <div style={row}>
          <span style={{ fontSize: 13, minWidth: '9em' }}><strong>Auto-diagnose</strong></span>
          <label style={{ margin: 0, fontSize: 13, fontWeight: 400, display: 'flex', gap: 6, alignItems: 'center' }}>
            <input type="checkbox" id="odau-diagnose" checked={d.autoDiagnose === true} disabled={d.autoDiagnose === null} onChange={(e) => post('/diagnose', { on: e.target.checked ? '1' : '0' }, sayDiag, () => onChange(), e.target.checked ? 'On' : 'Off')} />
            <span>critical alarms get a read-only diagnosis</span>
          </label>
          {diagMsg}
          <span className="text-muted" style={muted}>
            every minute, in the problem mode; at most 3 per company per minute, one per machine per hour.
            {d.autoDiagnose === null && <strong> The auto_diagnose column is missing.</strong>}
          </span>
        </div>
        <div style={row}>
          <label htmlFor="odau-cap" style={{ margin: 0, fontSize: 13, minWidth: '9em' }}><strong>Daily action cap</strong></label>
          <input type="number" min={0} className="form-control" id="odau-cap" style={{ width: 120 }} value={cap} disabled={d.dailyCap === null} onChange={(e) => setCap(e.target.value)} />
          <button type="button" className="btn btn-default btn-sm" id="odau-cap-save" disabled={d.dailyCap === null} onClick={() => post('/cap', { cap }, sayCap)}>Save</button>
          {capMsg}
          <span className="text-muted" style={muted}>playbook actions that are not shadow, per company, per day. 0 = no cap. A storm of alarms must not become a storm of restarts.</span>
        </div>
      </div>
    </div>
  )
}

/** Regulile: explicatia, lista (filtrata pe nivel), editorul, biblioteca, activitatea. */
function RulesSection({ d, level, onChange }: { d: Page; level: Level; onChange(): void }) {
  const [editing, setEditing] = useState<Row | null>(null)
  const mayWrite = d.isGlobal || d.canScope
  const companyLabel = d.companyName || `company #${d.companyId}`
  const visible = level === 'all' ? d.rules : d.rules.filter((r) => n(r.company_id) === 0 || n(r.company_id) === d.companyId)
  const platform = visible.filter((r) => n(r.company_id) === 0)
  const own = visible.filter((r) => n(r.company_id) !== 0)
  const recent = level === 'all' || !d.isGlobal ? d.recent : d.recent.filter((r) => r.company_id === undefined || n(r.company_id) === d.companyId)
  const card = (rule: Row) => <RuleCard key={n(rule.ID)} d={d} level={level} rule={rule} onEdit={() => setEditing(rule)} onChange={onChange} />

  return (
    <>
      <div className="odx-group-title">
        <strong>Rules</strong>
        {mayWrite ? (
          <button type="button" className="btn btn-primary btn-sm" id="odau-add" onClick={() => setEditing({ posture: 'shadow', match_severity: 'critical' })}>
            + New rule{level === 'all' ? ' (every company)' : ` (for ${d.companyName})`}
          </button>
        ) : (
          <span className="text-muted" style={muted}>per-company rules need the schema migration; until then only a Global Admin writes them</span>
        )}
      </div>

      <details className="odx-fold" id="odau-explain">
        <summary>How a rule works</summary>
        <div className="odx-fold-body" style={{ fontSize: 13 }}>
          <p style={{ margin: '0 0 8px' }}>
            A <strong>rule</strong> ties a pattern in the log to a pre-approved action:{' '}
            <em>&bdquo;if I see <strong>this</strong>, <strong>this many times</strong> within <strong>this many minutes</strong> on the same machine, then <strong>do that</strong>&rdquo;</em>.
            For example, <em>&bdquo;if the backup service dies three times in 30 minutes, restart it&rdquo;</em>.
          </p>
          <p style={{ margin: 0 }}>
            <strong>Posture</strong> decides how far it goes:{' '}
            <span style={{ color: '#6b7480' }}><strong>Shadow</strong> only writes down what it WOULD have done</span> &middot;{' '}
            <span style={{ color: '#1f5f8b' }}><strong>Propose</strong> prepares a proposal for you to approve</span> &middot;{' '}
            <span style={{ color: '#b42318' }}><strong>Remediate</strong> runs it there and then</span>. Start on Shadow, read in &bdquo;Recent activity&rdquo; what it would have done, and only then move up. The command always comes from the catalog of pre-approved actions, never from the agent.
          </p>
        </div>
      </details>

      {mayWrite && editing && <RuleEditor d={d} level={level} rule={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); void onChange() }} />}

      {visible.length === 0 && (
        <div className="panel panel-default">
          <div className="panel-body text-muted">
            No rules yet.{' '}
            {mayWrite ? (
              <>Press &bdquo;New rule&rdquo; or add one from the library below, and start on <strong>Shadow</strong> &mdash; it will not touch a thing until you say so.</>
            ) : (
              'The Global Admin has not written any rules yet.'
            )}
          </div>
        </div>
      )}
      {platform.length > 0 && level === 'company' && own.length > 0 && <div className="odx-sub-title text-muted" style={{ fontSize: 12, margin: '0 0 6px' }}>For every company</div>}
      {platform.map(card)}
      {own.length > 0 && (
        <div className="text-muted" style={{ fontSize: 12, margin: '12px 0 6px' }}>
          {level === 'all' ? 'Written for one company' : `Only for ${companyLabel}`}
        </div>
      )}
      {own.map(card)}

      <LibraryPanel d={d} level={level} onChange={onChange} />

      <div className="odx-group-title"><strong>Recent activity</strong></div>
      <div className="panel panel-default">
        <div className="panel-body">
          {recent.length === 0 ? (
            <span className="text-muted" style={muted}>Nothing has fired yet.</span>
          ) : (
            <div className="odau-table-wrap">
              <table className="table table-condensed odau-table" style={{ fontSize: 12, margin: 0, ...tableMin(36) }}>
                <colgroup><col style={{ width: '11em' }} /><col /><col style={{ width: '7em' }} /><col /><col style={{ width: '7.5em' }} /></colgroup>
                <thead><tr><th>When</th><th>Rule</th><th>Posture</th><th>Device</th><th>Proposal</th></tr></thead>
                <tbody>
                  {recent.map((r, i) => {
                    const pm = POSTURE[s(r.posture)] ?? POSTURE.shadow
                    return (
                      <tr key={i}>
                        <td style={{ whiteSpace: 'nowrap' }}>{s(r.fired_at)}</td>
                        <td>{s(r.rule_name)}</td>
                        <td><span style={{ color: pm[1] }}>{pm[0]}</span></td>
                        <td>{s(r.device) || `#${n(r.device_id)}`}</td>
                        <td>{n(r.draft_id) ? <a href="/app/change">#{n(r.draft_id)}</a> : <span className="text-muted">—</span>}</td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </>
  )
}

function SensorsPanel({ d }: { d: Page }) {
  const [msg, say] = useMsg()
  const [checked, setChecked] = useState<string[]>(d.sensors.filter((x) => Boolean(x.enabled)).map((x) => s(x.key)))
  return (
    <div className="panel panel-default">
      <div className="panel-body">
        <div style={{ display: 'flex', gap: 12, alignItems: 'baseline', flexWrap: 'wrap' }}>
          <strong style={{ fontSize: 14 }}>Sensors</strong>
          <span className="text-muted" style={muted}>
            states that become alarms - the machine went silent, a disk is almost full, the backup is missing. Each one writes ONE event when the state appears (module Opteemum, category Alarms, keyword = the sensor name) and one when it clears. Rules match them by keyword; critical ones get auto-diagnosed. Until this list is saved once, the quiet, cheap sensors run by default (silence, stopped service, disk full or filling, SMART, failed backup, critical patches); what you save here - even nothing - is what runs.
          </span>
        </div>
        {!d.sensorsReady ? (
          <div className="text-muted" style={{ fontSize: 12, marginTop: 8 }}>
            Needs the <code>2026_09_04_100001_autonomy_sensors</code> migration - apply it from <a href="/app/settings/change_log">Change Log &rarr; Database migrations</a>.
          </div>
        ) : (
          <>
            <div className="odau-table-wrap" style={{ marginTop: 10 }}>
              <table className="table table-condensed odau-table" style={{ fontSize: 12, margin: 0, ...tableMin(40) }}>
                <colgroup><col style={{ width: '3.5em' }} /><col style={{ width: '15em' }} /><col style={{ width: '6.5em' }} /><col /><col style={{ width: '7em' }} /></colgroup>
                <thead><tr><th>On</th><th>Sensor</th><th>Severity</th><th>What it watches</th><th style={{ textAlign: 'right' }}>Active now</th></tr></thead>
                <tbody>
                  {d.sensors.map((x) => {
                    const key = s(x.key)
                    return (
                      <tr key={key}>
                        <td><input type="checkbox" className="odau-sensor" value={key} checked={checked.includes(key)} disabled={!d.isGlobal} onChange={(e) => setChecked(e.target.checked ? [...checked, key] : checked.filter((k) => k !== key))} /></td>
                        <td><strong>{s(x.label)}</strong><div><code style={{ fontSize: 11 }}>{key}</code> {x.slow ? <span className="text-muted" style={{ fontSize: 11 }}>hourly</span> : null}</div></td>
                        <td><span style={{ fontWeight: 600, color: SEV_COLOR[s(x.severity)] ?? '#6b7480' }}>{s(x.severity)}</span></td>
                        <td className="text-muted">{s(x.what)}</td>
                        <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{n(x.active) > 0 ? <strong>{n(x.active)}</strong> : <span className="text-muted">0</span>}</td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
            {d.isGlobal ? (
              <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 8 }}>
                <button type="button" className="btn btn-primary btn-sm" id="odau-sensors-save" onClick={() => post('/sensors', { sensors: checked }, say)}>Save sensors</button>
                {msg}
                <span className="text-muted" style={muted}>A freshly turned-on sensor announces at most 50 machines per minute; the rest follow on the next ticks.</span>
              </div>
            ) : (
              <span className="text-muted" style={muted}>only a Global Admin can turn sensors on or off</span>
            )}
          </>
        )}
      </div>
    </div>
  )
}

function LibraryPanel({ d, level, onChange }: { d: Page; level: Level; onChange(): void }) {
  const mayWrite = d.isGlobal || d.canScope
  const count = Object.keys(d.library).length
  const [deskMsg, sayDesk] = useMsg()
  const seedDesk = () => post('/library/desk', { company_id: String(level === 'all' ? 0 : d.companyId) }, sayDesk, (r) => {
    const skipped = r.skipped?.length ?? 0
    sayDesk(`In the catalog: ${r.added ?? 0} new, ${r.ids?.length ?? 0} in total${skipped > 0 ? `; ${skipped} left alone (their command was edited)` : ''}`, 'ok')
    onChange()
  }, 'In the catalog')
  const pack = d.desk.desk_catalog
  return (
    <details className="odx-fold" id="odau-library" open={d.rules.length === 0}>
      <summary>Rule library <span className="text-muted" style={{ fontWeight: 400 }}>&middot; {count} ready-made rule{count === 1 ? '' : 's'}</span></summary>
      <div className="odx-fold-body">
        <div className="text-muted" style={muted}>
          The rules that matter in an infrastructure, on the sensors, with the right playbook. Each is added {level === 'all' ? 'for every company' : <>for <strong>{d.companyName || `company #${d.companyId}`}</strong></>}, off and in shadow: read what it would have done for a few days, then turn it up.
        </div>
        <div className="odau-table-wrap" style={{ marginTop: 10 }}>
          <table className="table table-condensed odau-table" style={{ fontSize: 12, margin: 0, ...tableMin(40) }}>
            <colgroup><col style={{ width: '20em' }} /><col /><col style={{ width: '9em' }} /></colgroup>
            <tbody>
              {Object.entries(d.library).map(([key, t]) => (
                <LibraryRow key={key} k={key} t={t} companyId={level === 'all' ? 0 : d.companyId} mayWrite={mayWrite} onChange={onChange} />
              ))}
            </tbody>
          </table>
        </div>
        <div id="odau-desk-catalog" className="text-muted" style={{ ...muted, marginTop: 10 }}>
          <strong>Service desk runbooks.</strong> The everyday fixes on a person&apos;s own machine - print spooler and queue, network printer, DNS and group policy, Outlook OST, Teams cache, VPN profile, Windows Update cache, pending reboot, safe disk cleanup, service restart - for Windows, and for Linux and macOS where they apply. They go in the vetted catalog incident-safe, but a Global Admin still has to tick each one as desk-executable under Settings &rarr; AI Agent &rarr; Runbooks before the service desk runs it. Pressing it again adds nothing.
          {pack && (
            <div id="odau-desk-catalog-count" style={{ marginTop: 6 }}>
              {pack.seeded === 0 ? (
                'No desk runbooks in the catalog yet.'
              ) : (
                <>
                  Now: <strong>{pack.seeded}</strong> desk runbook{pack.seeded === 1 ? '' : 's'}, <strong>{pack.executable}</strong> desk-executable
                  {pack.stale > 0 && <>, <strong style={{ color: '#a94442' }}>{pack.stale}</strong> ticked but changed since - tick {pack.stale === 1 ? 'it' : 'them'} again</>}
                </>
              )}
            </div>
          )}
          {mayWrite && (
            <div style={{ marginTop: 6 }}>
              <button type="button" className="btn btn-default btn-xs" id="odau-desk-seed" onClick={seedDesk}>
                Seed desk catalog {level === 'all' ? '(every company)' : `(for ${d.companyName || `#${d.companyId}`})`}
              </button>{' '}
              {deskMsg}
            </div>
          )}
        </div>
      </div>
    </details>
  )
}

function LibraryRow({ k, t, companyId, mayWrite, onChange }: { k: string; t: Row; companyId: number; mayWrite: boolean; onChange(): void }) {
  const [addMsg, sayAdd] = useMsg()
  const [rbMsg, sayRb] = useMsg()
  const pb = (t.playbook ?? {}) as Row
  const runbooks = (t.runbooks ?? {}) as Record<string, [string, string]>
  return (
    <tr data-key={k}>
      <td>
        <strong>{s(t.name)}</strong>
        <div><code style={{ fontSize: 11 }}>{s(t.sensor)}</code> <span className="text-muted">{s(t.severity)} &middot; {s(pb.action)}{s(t.command_type) !== '' ? ` ${s(t.command_type)}` : ''} &middot; else {s(pb.escalate)}</span></div>
      </td>
      <td className="text-muted">
        {s(t.why)}
        {Object.keys(runbooks).length > 0 && (
          <div style={{ marginTop: 4 }}>
            {Object.entries(runbooks).map(([os, rb]) => (
              <div key={os}><strong>{os.charAt(0).toUpperCase() + os.slice(1)}</strong> &middot; {rb[0]}: <code style={{ fontSize: 11, whiteSpace: 'pre-wrap' }}>{rb[1]}</code></div>
            ))}
            <span style={{ fontSize: 11 }}>
              {mayWrite && (
                <>
                  <button type="button" className="btn btn-default btn-xs odau-lib-runbooks" onClick={() => post('/library/runbooks', { key: k, company_id: String(companyId) }, sayRb, undefined, 'In the catalog')}>Put them in the catalog, incident-safe</button>{' '}
                  {rbMsg} &middot;{' '}
                </>
              )}
              or copy them into the vetted catalog by hand and mark them incident-safe under Settings &rarr; AI Agent &rarr; Runbooks. Once there, the rule, the alarm response and the service desk can run them.
            </span>
          </div>
        )}
      </td>
      <td style={{ textAlign: 'right' }}>
        {mayWrite && (
          <>
            <button type="button" className="btn btn-default btn-xs odau-lib-add" onClick={() => post('/library/add', { key: k, company_id: String(companyId) }, sayAdd, () => setTimeout(onChange, 700), 'Added (off, shadow)')}>Add</button>
            <div style={{ fontSize: 11, marginTop: 3 }}>{addMsg}</div>
          </>
        )}
      </td>
    </tr>
  )
}

function RuleCard({ d, level, rule, onEdit, onChange }: { d: Page; level: Level; rule: Row; onEdit(): void; onChange(): void }) {
  const [msg, say] = useMsg()
  const id = n(rule.ID)
  const ruleCompany = n(rule.company_id)
  const isOwn = ruleCompany > 0 && ruleCompany === d.companyId
  const mayEdit = level === 'all' ? d.isGlobal : isOwn
  const showOverride = level === 'company' && !isOwn
  const ov = !showOverride ? null : (d.overrides[String(id)] ?? null)
  const effPosture = ov && ['shadow', 'propose', 'remediate'].includes(s(ov.posture)) ? s(ov.posture) : s(rule.posture)
  const pm = POSTURE[effPosture] ?? POSTURE.shadow
  const offHere = Boolean(ov) && n(ov?.is_off) === 1
  const [ovOff, setOvOff] = useState(offHere)
  const [ovPosture, setOvPosture] = useState(ov ? s(ov.posture) : '')
  const win: string[] = []
  if (s(rule.active_days)) {
    const names: Record<string, string> = Object.fromEntries(DAYS)
    win.push(s(rule.active_days).split(',').map((x) => names[x] ?? x).join('/'))
  }
  if (s(rule.active_from) && s(rule.active_to)) win.push(`${s(rule.active_from).slice(0, 5)}–${s(rule.active_to).slice(0, 5)}`)

  return (
    <div className="panel panel-default">
      <div className="panel-body">
        <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
          <div style={{ flex: '1 1 auto', minWidth: 0 }}>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
              <strong style={{ fontSize: 14 }}>{s(rule.name)}</strong>
              <span style={pill(pm[2], pm[1])}>{pm[0]}</span>
              {ruleCompany === 0 ? (
                <span title="A platform rule - it applies to every company" style={{ ...pill('#eef0f2', '#3d4752'), fontWeight: 400 }}><i className="fas fa-globe" aria-hidden="true"></i> the whole platform</span>
              ) : isOwn ? (
                <span title="Your company's rule - you can change it directly" style={{ ...pill('#e3f0fa', '#1f5f8b'), fontWeight: 400 }}>{d.companyName}</span>
              ) : (
                <span style={{ ...pill('#eef0f2', '#5b6672'), fontWeight: 400 }}>company #{ruleCompany}</span>
              )}
              {n(rule.enabled) !== 1 ? (
                <span style={{ ...pill('#eef0f2', '#5b6672'), fontWeight: 400 }}>disabled globally</span>
              ) : offHere ? (
                <span style={{ ...pill('#fdecea', '#b42318'), fontWeight: 400 }}>off for this company</span>
              ) : (
                <span style={{ ...pill('#e6f4e6', '#2e7d32'), fontWeight: 400 }}>{level === 'all' ? 'on' : 'live here'}</span>
              )}
            </div>
            <div className="text-muted" style={{ fontSize: 12, marginTop: 6 }}>
              when <strong>{s(rule.match_severity)}</strong>
              {s(rule.match_module) && <> · {s(rule.match_module)}</>}
              {s(rule.match_category) && <>/ {s(rule.match_category)}</>}
              {s(rule.match_keyword) && <> · &ldquo;{s(rule.match_keyword)}&rdquo;</>}
              {' '}&middot; {n(rule.threshold_count)}&times; in {n(rule.threshold_minutes)}min &rarr; run <code>{s(rule.command_type)}</code>
              {s(rule.playbook_summary) && <><br /><span style={{ color: '#1f5f8b' }}>playbook:</span> {s(rule.playbook_summary)}</>}
              {' '}&middot; cooldown {n(rule.cooldown_minutes)}min, max {n(rule.max_per_hour)}/h, {n(rule.max_devices_per_run)}/run
              {win.length > 0 && <> &middot; <span style={{ color: '#8a6116' }}>window: {win.join(' ')}</span></>}
              {s(rule.description) && <div>{s(rule.description)}</div>}
            </div>
            {showOverride && (
              <div style={{ marginTop: 10, padding: '8px 10px', background: '#f6f7f9', borderRadius: 6, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                <span style={{ fontSize: 12, fontWeight: 600 }}>For {d.companyName}:</span>
                <label style={{ margin: 0, fontSize: 12, fontWeight: 400 }}><input type="checkbox" className="odau-ov-off" checked={ovOff} onChange={(e) => setOvOff(e.target.checked)} /> off here</label>
                <label style={{ margin: 0, fontSize: 12, fontWeight: 400 }}>
                  posture:{' '}
                  <select className="odau-ov-posture" style={{ fontSize: 12 }} value={ovPosture} onChange={(e) => setOvPosture(e.target.value)}>
                    <option value="">inherit ({s(rule.posture)})</option>
                    <option value="shadow">shadow</option>
                    <option value="propose">propose</option>
                    <option value="remediate">remediate</option>
                  </select>
                </label>
                <button type="button" className="btn btn-default btn-xs odau-ov-save" onClick={() => post('/rules/override', { rule_id: String(id), is_off: ovOff ? '1' : '0', posture: ovPosture }, say, () => onChange())}>Save for company</button>
                {msg}
              </div>
            )}
          </div>
          {mayEdit && (
            <div style={{ flex: '0 0 auto', textAlign: 'right' }}>
              <button type="button" className="btn btn-default btn-xs odau-edit" onClick={onEdit}>Edit</button>{' '}
              <button type="button" className="btn btn-default btn-xs odau-del" onClick={() => {
                if (!window.confirm('Delete the global rule? (per-company overrides go with it; the history stays)')) return
                post('/rules/delete', { id: String(id) }, say, () => onChange(), 'Deleted')
              }}>Delete</button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

function RuleEditor({ d, level, rule, onClose, onSaved }: { d: Page; level: Level; rule: Row; onClose(): void; onSaved(): void }) {
  let pb: Row = {}
  try { pb = (JSON.parse(s(rule.playbook_json) || '{}') || {}) as Row } catch { pb = {} }
  const f = (k: string, def: string) => (rule[k] === undefined || rule[k] === null ? def : s(rule[k]))
  const [v, setV] = useState<Record<string, string>>({
    id: f('ID', '0'), name: f('name', ''), description: f('description', ''), enabled: n(rule.enabled) === 1 ? '1' : '0',
    posture: f('posture', 'shadow'), match_module: f('match_module', ''), match_category: f('match_category', ''),
    match_severity: f('match_severity', 'critical'), match_keyword: f('match_keyword', ''),
    threshold_count: f('threshold_count', '1'), threshold_minutes: f('threshold_minutes', '15'), command_type: f('command_type', ''),
    cooldown_minutes: f('cooldown_minutes', '60'), max_per_hour: f('max_per_hour', '5'), max_devices_per_run: f('max_devices_per_run', '5'),
    active_days: f('active_days', ''), active_from: f('active_from', '').slice(0, 5), active_to: f('active_to', '').slice(0, 5),
    pb_on: String(pb.on) === '1' ? '1' : '0', pb_diagnose: pb.diagnose === undefined ? '1' : String(pb.diagnose) === '1' ? '1' : '0',
    pb_action: s(pb.action) || 'catalog', pb_verify: pb.verify_minutes === undefined ? '10' : s(pb.verify_minutes), pb_escalate: s(pb.escalate) || 'ticket',
    pb_servers: pb.protect_servers === undefined ? '1' : String(pb.protect_servers) === '1' ? '1' : '0',
    pb_rb_win: s(pb.runbook_windows) || '0', pb_rb_lin: s(pb.runbook_linux) || '0',
    pb_deps: pb.need_dependents === undefined ? '1' : String(pb.need_dependents) === '1' ? '1' : '0',
  })
  const [msg, say] = useMsg()
  const [preview, setPreview] = useState<ReactNode>(null)
  const set = (k: string, val: string) => setV({ ...v, [k]: val })
  const days = v.active_days ? v.active_days.split(',') : []
  const fields = (): Record<string, PostValue> => ({
    id: v.id, name: v.name, description: v.description, enabled: v.enabled, posture: v.posture,
    match_module: v.match_module, match_category: v.match_category, match_severity: v.match_severity, match_keyword: v.match_keyword,
    threshold_count: v.threshold_count, threshold_minutes: v.threshold_minutes, command_type: v.command_type, cooldown_minutes: v.cooldown_minutes,
    max_per_hour: v.max_per_hour, max_devices_per_run: v.max_devices_per_run, active_days: v.active_days, active_from: v.active_from, active_to: v.active_to,
    'playbook[on]': v.pb_on, 'playbook[diagnose]': v.pb_diagnose, 'playbook[action]': v.pb_action, 'playbook[verify_minutes]': v.pb_verify,
    'playbook[escalate]': v.pb_escalate, 'playbook[protect_servers]': v.pb_servers, 'playbook[runbook_windows]': v.pb_rb_win,
    'playbook[runbook_linux]': v.pb_rb_lin, 'playbook[need_dependents]': v.pb_deps,
    ...(n(v.id) > 0 ? {} : { company_id: String(level === 'all' ? 0 : d.companyId) }),
  })
  const input = (k: string, type = 'text', extra: Record<string, unknown> = {}) => <input type={type} className="form-control" id={`odau-f-${k}`} value={v[k]} onChange={(e) => set(k, e.target.value)} {...extra} />
  const check = (k: string, id: string) => <input type="checkbox" id={id} checked={v[k] === '1'} onChange={(e) => set(k, e.target.checked ? '1' : '0')} />

  return (
    <div className="panel panel-default" id="odau-editor" style={{ borderLeft: '4px solid #1f5f8b' }}>
      <div className="panel-body">
        <div className="row">
          <div className="form-group col-md-6"><label>Name</label>{input('name')}</div>
          <div className="form-group col-md-6"><label>Description</label>{input('description')}</div>
        </div>
        <div className="row">
          <div className="form-group col-md-3"><label>Severity</label>
            <select className="form-control" id="odau-f-sev" value={v.match_severity} onChange={(e) => set('match_severity', e.target.value)}>
              <option value="critical">critical</option><option value="major">major</option><option value="minor">minor</option><option value="info">info</option>
            </select></div>
          <div className="form-group col-md-3"><label>Module</label>{input('match_module', 'text', { placeholder: 'any' })}</div>
          <div className="form-group col-md-3"><label>Category</label>{input('match_category', 'text', { placeholder: 'any' })}</div>
          <div className="form-group col-md-3"><label>Keyword</label>{input('match_keyword', 'text', { placeholder: 'any' })}</div>
        </div>
        <div className="row">
          <div className="form-group col-md-3"><label>Threshold count</label>{input('threshold_count', 'number', { min: 1 })}</div>
          <div className="form-group col-md-3"><label>Within minutes</label>{input('threshold_minutes', 'number', { min: 1 })}</div>
          <div className="form-group col-md-3"><label>Action (incident_safe)</label>
            <select className="form-control" id="odau-f-cmd" value={v.command_type} onChange={(e) => set('command_type', e.target.value)}>
              <option value="">-- pick --</option>
              {d.catalog.map((c) => <option key={s(c.command_type)} value={s(c.command_type)}>{s(c.command_type)} — {s(c.command_title)}</option>)}
            </select></div>
          <div className="form-group col-md-3"><label>Posture</label>
            <select className="form-control" id="odau-f-posture" value={v.posture} onChange={(e) => set('posture', e.target.value)}>
              <option value="shadow">shadow (log only)</option><option value="propose">propose (human approves)</option><option value="remediate">remediate (run now)</option>
            </select></div>
        </div>
        <div className="row">
          <div className="form-group col-md-3"><label>Cooldown (min)</label>{input('cooldown_minutes', 'number', { min: 1 })}</div>
          <div className="form-group col-md-3"><label>Max per hour</label>{input('max_per_hour', 'number', { min: 1 })}</div>
          <div className="form-group col-md-3"><label>Max devices / run</label>{input('max_devices_per_run', 'number', { min: 1 })}</div>
          <div className="form-group col-md-3"><label style={{ display: 'block' }}>Enabled</label>{check('enabled', 'odau-f-enabled')} <span className="text-muted" style={muted}>globally</span></div>
        </div>
        <div className="row">
          <div className="form-group col-md-6">
            <label>Maintenance window &mdash; days</label>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', fontSize: 12 }}>
              {DAYS.map(([num, lbl]) => (
                <label key={num} style={{ margin: 0, fontWeight: 400 }}>
                  <input type="checkbox" className="odau-f-day" value={num} checked={days.includes(num)} onChange={(e) => set('active_days', (e.target.checked ? [...days, num] : days.filter((x) => x !== num)).sort().join(','))} /> {lbl}
                </label>
              ))}
            </div>
            <span className="text-muted" style={{ fontSize: 11 }}>nothing ticked = any day</span>
          </div>
          <div className="form-group col-md-3"><label>From (HH:MM)</label>{input('active_from', 'time')}</div>
          <div className="form-group col-md-3"><label>To (HH:MM)</label>{input('active_to', 'time')}</div>
          <div className="col-md-12"><span className="text-muted" style={{ fontSize: 11 }}>Empty hours = all day. From &gt; To crosses midnight (e.g. 22:00&ndash;06:00). The window is in server time.</span></div>
        </div>
        <div className="row" style={{ borderTop: '1px solid #eef0f2', marginTop: 4, paddingTop: 10 }}>
          <div className="col-md-12" style={{ marginBottom: 6 }}>
            <label style={{ margin: 0 }}><input type="checkbox" id="odau-f-pb-on" checked={v.pb_on === '1'} disabled={!d.playbookReady} onChange={(e) => set('pb_on', e.target.checked ? '1' : '0')} /> <strong>Playbook</strong></label>{' '}
            <span className="text-muted" style={muted}>
              diagnose &rarr; act (with guards) &rarr; verify on the next ticks &rarr; escalate if it persists; twice failed on a machine in a day = the rule stops there.
              {!d.playbookReady && <strong> Needs the 2026_09_04_100002_autonomy_playbook migration.</strong>}
            </span>
          </div>
          <div className="form-group col-md-3"><label style={{ display: 'block' }}>Diagnose first</label>{check('pb_diagnose', 'odau-f-pb-diag')} <span className="text-muted" style={muted}>the agent, read-only</span></div>
          <div className="form-group col-md-3"><label>Action</label>
            <select className="form-control" id="odau-f-pb-action" value={v.pb_action} onChange={(e) => set('pb_action', e.target.value)}>
              <option value="catalog">catalog command (above)</option><option value="runbook">a runbook from the vetted catalog, by OS</option><option value="agent">the agent fixes it (incident-safe)</option><option value="none">diagnosis only</option>
            </select></div>
          <div className="form-group col-md-3"><label>Verify after (min)</label>{input('pb_verify', 'number', { min: 0 })}</div>
          <div className="form-group col-md-3"><label>If not fixed</label>
            <select className="form-control" id="odau-f-pb-esc" value={v.pb_escalate} onChange={(e) => set('pb_escalate', e.target.value)}>
              <option value="ticket">open a ticket</option><option value="propose">propose it (human approves)</option><option value="notify">mail the notify address</option><option value="none">nothing</option>
            </select></div>
          {v.pb_action === 'runbook' && (
            <>
              <div className="form-group col-md-6 odau-pb-rb"><label>Runbook on Windows</label>
                <select className="form-control" id="odau-f-pb-rb-win" value={v.pb_rb_win} onChange={(e) => set('pb_rb_win', e.target.value)}>
                  <option value="0">-- pick an incident-safe runbook --</option>
                  {d.runbooks.map((rb) => <option key={n(rb.ID)} value={String(n(rb.ID))}>{s(rb.command_title)}</option>)}
                </select></div>
              <div className="form-group col-md-6 odau-pb-rb"><label>Runbook on Linux / macOS</label>
                <select className="form-control" id="odau-f-pb-rb-lin" value={v.pb_rb_lin} onChange={(e) => set('pb_rb_lin', e.target.value)}>
                  <option value="0">-- pick an incident-safe runbook --</option>
                  {d.runbooks.map((rb) => <option key={n(rb.ID)} value={String(n(rb.ID))}>{s(rb.command_title)}</option>)}
                </select>
                <span className="text-muted" style={{ fontSize: 11 }}>runbooks are marked incident-safe under <a href="/app/settings/ai_agent#aiaRunbooks">Settings &rarr; AI Agent &rarr; Runbooks</a></span></div>
            </>
          )}
          <div className="col-md-12" style={{ fontSize: 12 }}>
            <label style={{ fontWeight: 400, marginRight: 14 }}>{check('pb_servers', 'odau-f-pb-servers')} on servers and hosts, never remediate - propose</label>
            <label style={{ fontWeight: 400 }}>{check('pb_deps', 'odau-f-pb-deps')} if the CMDB says something depends on the machine, propose instead of remediate</label>
            <div className="text-muted" style={{ marginTop: 4 }}>Catalog commands may carry <code>{'{{service}}'}</code> or <code>{'{{mount}}'}</code>: the playbook fills them from the sensor alarm (e.g. the stopped service&apos;s name).</div>
          </div>
        </div>
        {d.catalog.length === 0 && (
          <div className="text-muted" style={{ fontSize: 12, marginBottom: 8 }}>
            No incident_safe action in the catalog yet - mark one under <a href="/app/settings/ai_agent">Settings &rarr; AI Agent</a> so a rule has something to aim at.
          </div>
        )}
        <button type="button" className="btn btn-primary btn-sm" id="odau-save" onClick={() => post('/rules', fields(), say, () => onSaved(), 'Saved')}>Save rule</button>{' '}
        <button type="button" className="btn btn-default btn-sm" id="odau-preview" onClick={() => {
          setPreview(<span className="text-muted">Looking...</span>)
          void autonomyPost('/rules/preview', fields()).then((r) => {
            if (!r.ok) { setPreview(<span style={{ color: '#b42318' }}>{r.error || 'Preview failed.'}</span>); return }
            if (!r.count) { setPreview(<span className="text-muted">No machine matches this rule right now (within the threshold window).</span>); return }
            setPreview(<>
              <div style={{ marginBottom: 6 }}><strong>{r.count}</strong> machine(s) would match right now:</div>
              {(r.hits ?? []).map((h) => <div key={h.device_id}>· {h.device || `#${h.device_id}`} <span className="text-muted">({h.n} events)</span></div>)}
            </>)
          }).catch(() => setPreview(<span style={{ color: '#b42318' }}>Cererea a picat.</span>))
        }}>Preview matches now</button>{' '}
        <button type="button" className="btn btn-default btn-sm" id="odau-cancel" onClick={onClose}>Cancel</button>{' '}
        <span style={{ marginLeft: 8 }}>{msg}</span>
        {preview && <div id="odau-preview-out" style={{ marginTop: 10, fontSize: 12, background: '#f6f7f9', borderRadius: 6, padding: '8px 10px', maxHeight: 220, overflow: 'auto' }}>{preview}</div>}
      </div>
    </div>
  )
}
